import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { injectIntl } from 'react-intl';
import ReactDOM from 'react-dom';

import FormInputLabel from './form-input-label';
import FormInputErrorMessage from './form-input-error-message';
import FormInputCorrectMessage from './form-input-correct-message';

import { getFormInputClasses, setErrorWidth } from '../../utilities/form-helpers';
import { isSameArrays } from '../../utilities/helpers';
import TranslatedCopy from '../../intl/translated-copy';

const mapStateToProps = state => {
  return {
    languageBundle: state.LanguageBundle.languageBundle,
    helperTraslatedCopy: state.LanguageBundle.helperTraslatedCopy,
  };
};

class FormInputDropdown extends Component {
  static defaultProps = {
    autoFocusInput: false,
    hasLabelTooltip: false,
    isClearable: false,
    isDisabled: false,
    isHidden: false,
    isRequired: false,
    placeholderCopyID: 'APP_SELECT',
    shouldSetErrorWidth: false,
    validateNoSpecialCharsOnChange: false,
    showIcon: false,
  }

  constructor(props) {
    super(props);

    const { defaultOption, isRequired } = this.props;
    const isDefaultOption = typeof defaultOption !== 'undefined';
    let selectedOption = null;

    if (isRequired) {
      selectedOption = isDefaultOption ? defaultOption : 0;
    } else if (isDefaultOption) {
      selectedOption = defaultOption;
    }

    this.state = {
      errorWidth: null,
      selectedOption: selectedOption,
      renderedOptions: this.renderSelectOptions(),
    };
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
  }

  componentWillMount() {
    const {
      options,
      input: { onBlur, value },
      meta: { touched },
      valueOption,
    } = this.props;
    const { selectedOption } = this.state;

    let updatedSelectedOption = selectedOption;
    if (value || valueOption) {
      const valueSelect = value || valueOption;
      const selectedIdx = options.findIndex((option) => option.value === valueSelect);
      if (selectedIdx && selectedIdx !== -1) {
        updatedSelectedOption = selectedIdx;
      }

      // If field has an initial value set, call onBlur so field
      // is 'touched', and validation state will be visible
      if (!touched) {
        onBlur();
      }
    }

    this.setState({
      selectedOption: updatedSelectedOption,
    });
  }

  componentDidMount() {
    const {
      input: {
        onChange,
        value,
      },
      options,
    } = this.props;

    const {
      selectedOption,
    } = this.state;

    const option = options[selectedOption];
    const valueSelected = option && option.value ? option.value : value;

    // Initial value
    onChange(valueSelected);
  }

  componentWillReceiveProps(nextProps) {
    const {
      defaultOption,
      options,
    } = this.props;

    // When options are changed we make selectedOption/selectedIndex null
    // If there is a case where you switch from US to CA, state selectedIndex would not match
    if (!isSameArrays(options, nextProps.options)) {
      this.setState({
        renderedOptions: this.renderSelectOptions(nextProps.options),
        selectedOption: defaultOption ? defaultOption : 0,
      });
    }
  }

  componentDidUpdate() {
    const { shouldSetErrorWidth } = this.props;
    const { errorWidth } = this.state;

    // Set error width (prevents error message being truncated)
    if (this.formInputErrorDOM && shouldSetErrorWidth) {
      const newErrorWidth = setErrorWidth(this.formInputErrorDOM.getBoundingClientRect().left);

      if (newErrorWidth !== errorWidth) {
        this.setState({
          errorWidth: newErrorWidth,
        });
      }
    }
  }

  render() {
    const {
      className,
      isDisabled,
      isErrorInForm,
      isHidden,
      isRequired,
      input: {
        name,
        value,
      },
      inputClassModifier,
      label,
      labelCopyID,
      labelClassModifier,
      ifDisabled,
      meta: {
        error,
        touched,
        valid,
      },
      validateNoSpecialCharsOnChange,
      showOptionalLabel,
    } = this.props;

    const { errorWidth, inFocus } = this.state;

    let isError = (error && touched) || isErrorInForm;
    const isValid = valid && value;

    if (validateNoSpecialCharsOnChange && !isError) {
      isError = error && error.includes('NO_SPECIAL');
    }

    const formFieldClasses = cx({
      'FormInput-wrapper': true,
      'is-disabled': ifDisabled,
      'is-error': isError,
      'is-correct': isValid,
      'is-inFocus': inFocus,
      'disabled': isDisabled,
    }, className);

    return (
      <div className={ getFormInputClasses(inputClassModifier, isHidden) }>
        <div className={ formFieldClasses } >
          <FormInputLabel
            name={ name }
            isRequired={ isRequired }
            label={ label }
            labelClassModifier={ labelClassModifier }
            labelCopyID={ labelCopyID }
            showOptionalLabel={ showOptionalLabel } />
          { this.renderSelect() }
          { isError && !isErrorInForm ?
            <FormInputErrorMessage
              name={ name }
              error={ error }
              errorWidth={ errorWidth }
              ref={ (c) => this.formInputErrorDOM = ReactDOM.findDOMNode(c) } /> : null }
          { (isValid && (name != 'gender')) &&
            <FormInputCorrectMessage value={ value } />
          }
        </div>
      </div>
    );
  }

  renderSelect() {
    const {
      autocompleteName,
      autoFocusInput,
      ifDisabled,
      classNamed,
      input: {
        name,
        value,
      },
      options,
      placeholderCopyID,
      tabIndex,
    } = this.props;

    const {
      selectedOption,
      renderedOptions,
    } = this.state;

    const isOptionSelected = selectedOption !== null && selectedOption !== undefined && selectedOption !== '';
    const option = options[selectedOption];
    const triggerCopyID = isOptionSelected && option && option.copyID ?
      option.copyID : placeholderCopyID;

    const valueClasses = cx({
      'FormInputDropdown-value': true,
      'is-inactive': selectedOption === null || false,
    });

    const defaultClases = cx({
      'FormInputDropdown-select': true,
      'disabled': ifDisabled,
    });

    return (
      <div className={ `FormInputDropdown-wrapper ${classNamed}` }>
        <div className='FormInputDropdown-listTrigger'>
          <span className={ valueClasses }>
            { (isOptionSelected && option?.copyID) || !isOptionSelected ?
              <TranslatedCopy copyID={ triggerCopyID } /> : option?.name }
          </span>
          <span className='FormInputDropdown-icon Icon Icon--arrowSmallDown' />
        </div>
        <select
          autoFocus={ autoFocusInput }
          aria-hidden={ true }
          autoComplete={ autocompleteName }
          className={ defaultClases }
          name={ name }
          onBlur={ this.handleBlur }
          onChange={ (e) => this.handleOptionSelect(e) }
          onFocus={ this.handleFocus }
          tabIndex={ tabIndex }
          value={ option && option.value ? option.value : value }
          disabled={ ifDisabled }>
          { renderedOptions }
        </select>
      </div>);
  }

  renderSelectOptions(options) {
    const { helperTraslatedCopy } = this.props;

    const optionsArray = options ? options : this.props.options;

    return optionsArray.map((option, i) => (
      <option key={ i } value={ option.value }>
        {
          option.copyID ? helperTraslatedCopy && helperTraslatedCopy.getFormatMessageFromcopyId(option.copyID) : option.name
        }
      </option>
    ));
  }

  handleBlur() {
    const {
      input: {
        onBlur,
      },
    } = this.props;

    this.setState({
      inFocus: false,
    });

    onBlur();
  }

  handleFocus() {
    this.setState({
      inFocus: true,
    });
  }

  getIndex(value) {
    let key = -1;
    key = this.state.renderedOptions.findIndex((element) => {
      if (element.props.value === value) {
        return element.key;
      }
    });
    return key;
  }

  handleOptionSelect = (e) => {
    const {
      input: {
        onChange,
      },
    } = this.props;

    let { selectedOption } = this.state;
    selectedOption = this.getIndex(e.target.value);

    onChange(e, e.target.value);

    this.setState({
      selectedOption: selectedOption,
    });
  }
}

FormInputDropdown = connect(mapStateToProps)(FormInputDropdown);

export default injectIntl(FormInputDropdown);
